import { Component, Input, OnInit } from '@angular/core';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ActivatedRoute, Router } from '@angular/router';
import {AbstractControl, AsyncValidatorFn, UntypedFormBuilder, ValidationErrors, Validators} from '@angular/forms';
import { CountryDetails } from '@supoui/shared/model/country';
import { DropdownDictionary } from '@supoui/shared/model/dropdownDictionary';
import { SupplierProfile } from '@supoui/shared/model/supplier-profile';
import { AccountService } from '@supoui/core/auth/account.service';
import { UserService } from '@supoui/core/user/user.service';
import { SupoCaseInvoiceService } from '@supoui/core/services/supo-case-invoice.service';
import { RequestUserInformation } from '@supoui/shared/model/request-user-information';
import { AbstractFormGroupComponent } from '../abstract-form-group.component';
import { SendFormSuccessModalComponent } from '../../../new-case/components/send-form-success-modal/send-form-success-modal.component';
import { REDIRECT_TIME } from '@supoui/shared/constants/application.constants';
import {Observable, of, Subscription, timer} from 'rxjs';
import { UntilDestroy } from '@ngneat/until-destroy';
import {catchError, map} from "rxjs/operators";

interface mobileInput {
  code: string;
  prefix: string;
  number: number;
}

@Component({
    selector: 'app-send-form-modal',
    templateUrl: 'send-form-modal.component.html',
    styleUrls: ['./send-form-modal.component.scss'],
    standalone: false
})
@UntilDestroy({ checkProperties: true })
export class SendFormModalComponent extends AbstractFormGroupComponent implements OnInit {
  @Input() public firstName: string;
  @Input() public mobile: mobileInput;
  @Input() public onBehalfOf: string;
  @Input() public refNo: string;

  supplierSelect: DropdownDictionary[] = [];
  private supplierProfiles: SupplierProfile[] = [];

  private redirecTimerSubscr: Subscription;

  constructor(
    public activeModal: NgbActiveModal,
    private router: Router,
    private route: ActivatedRoute,
    private fb: UntypedFormBuilder,
    private accountService: AccountService,
    private userService: UserService,
    private supoCaseInvoiceService: SupoCaseInvoiceService,
    private modalService: NgbModal
  ) {
    super();
  }

  ngOnInit(): void {
    this.fg = this.fb.group({
      firstName: [this.firstName, [Validators.required]],
      mobilePhoneCountryCode: [this.mobile?.code, [Validators.required]],
      mobilePhonePrefix: [this.mobile?.prefix, [Validators.required]],
      mobilePhone:
        [this.mobile?.number, {validators: [Validators.required, Validators.pattern('^[0-9]*$')], asyncValidators: [this.validatePhoneNumber()], updateOn: 'blur'}],
      onBehalfOf: [this.onBehalfOf, [Validators.required]],
      refNo: [this.refNo]
    });

    this.supplierProfiles = [];
    const isAdmin = this.accountService.isAdmin();
    this.userService.supplierProfiles(true).subscribe(allSuppliers => {
      this.supplierProfiles = allSuppliers;
      this.supplierSelect = allSuppliers.map(
        s => new DropdownDictionary(s.supplierProfileCode, s.supplierProfileName + ' - ' + s.supplierProfileCode)
      );
      if (!isAdmin) {
        if (this.supplierProfiles.length === 1) {
          this.fg.controls.onBehalfOf.disable();
          this.fg.controls.onBehalfOf.patchValue(this.supplierProfiles[0].supplierProfileCode);
        }
      }
    });
  }

  private validatePhoneNumber(): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      const phoneNumber = control.value;
      const countryCode = control.parent?.get('mobilePhonePrefix')?.value;
      if (!phoneNumber || !countryCode) {
        return of(null);
      }
      return this.supoCaseInvoiceService.validatePhoneNumber(countryCode, phoneNumber).pipe(
        map(res => (res && res.isValid ? null : { invalid: true })),
        catchError(() => of(null))
      );
    };
  }

  submitForm() {
    if (this.fg.valid) {
      const values = this.fg.getRawValue();

      const request = new RequestUserInformation({
        firstName: values.firstName,
        surname: values.surname,
        mobilePhoneCountryCode: values.mobilePhoneCountryCode,
        mobilePhonePrefix: values.mobilePhonePrefix,
        mobilePhoneNumber: values.mobilePhone,
        supplierProfileCode: values.onBehalfOf,
        refNo: values.refNo
      });

      this.supoCaseInvoiceService.sendInformationRequestToEnduser(request).subscribe(
        () => this.sendInformationSuccess(),
        err => (this.error = err)
      );
    } else {
      this.validateAllFormFields(this.fg);
    }
  }

  sendInformationSuccess(): void {
    const modalRef = this.modalService.open(SendFormSuccessModalComponent, {
      size: 'md',
      centered: true,
      scrollable: false
    });
    modalRef.result.then(result => {
      this.redirectToHome();
    });
    this.redirecTimerSubscr = timer(REDIRECT_TIME).subscribe(() => {
      modalRef.close();
      this.redirectToHome();
    });
  }

  closeModalEvent(result?: boolean): void {
    this.activeModal.close(result);
  }

  endUserMobilePhoneCountryChanged(country: CountryDetails): void {
    this.fg.controls.mobilePhonePrefix.patchValue(country.phonePrefix);
    this.fg.controls.mobilePhone.updateValueAndValidity();
  }

  private redirectToHome() {
    this.closeModalEvent(true);
    this.router.navigate(['/', 'home']);
  }
}
